import BaseModel from "./BaseModel";

import {
    action,
    computed,
    makeObservable,
    observable,
    reaction,
} from "mobx";

import {isValidPhoneNumber} from "libphonenumber-js";

import Person from "./Person";

import {post} from "../common/requester";


const isEmpty = (value) => {
    return (
        value === null ||
        value === undefined ||
        value === ""
    );
};


const normalizePhone = (value) => {
    if (!value) {
        return "";
    }
    const digits =
        String(value).replace(/\D/g, "");
    return digits
        ? `+${digits}`
        : "";
};


export default class Member extends BaseModel {
    constructor(store) {
        super(store);
        makeObservable(this, {
            id: observable,

            title: observable,
            has_title: observable,

            first_name: observable,
            last_name: observable,
            phone_number: observable,

            user: observable,

            company: observable,
            company_id: observable,

            role_id: observable,
            role: observable,

            date_from: observable,
            date_to: observable,

            mother_id: observable,
            mother: observable,

            father_id: observable,
            father: observable,

            children: observable,

            forma_number: observable,
            year_born: observable,
            discount_amount: observable,

            teams: observable,

            is_archived: observable,

            photo_upload: observable,
            avatar: observable,

            was_validated: observable,

            is_father: observable,
            is_mother: observable,

            is_valid_phone_number: computed,
            is_role_valid: computed,
            is_valid: computed,

            validate: action,

            upload_avatar: action,
            avatar_valid: computed,
            
            awards: observable,
            award_count: observable,
        });


        reaction(
            () => this.first_name,

            () => {
                this.updateAutoTitle();
            }
        );


        reaction(
            () => this.last_name,

            () => {
                this.updateAutoTitle();
            }
        );
    }


    id = 0;

    title = "";
    has_title = false;

    first_name = "";
    last_name = "";
    phone_number = "";

    user = null;

    company_id = 0;
    company = {};

    role_id = "";
    role = null;

    date_from = null;
    date_to = null;

    mother_id = null;
    mother = new Person("mother");

    father_id = null;
    father = new Person("father");

    children = [];

    forma_number = null;
    year_born = null;
    discount_amount = null;

    teams = [];

    is_archived = false;

    photo_upload = null;

    avatar =
        "https://placehold.co/200?text=No+Photo&font=roboto";

    was_validated = false;

    is_father = false;
    is_mother = false;
    
    awards = []
    award_count = 0

    /* =====================================================
       AUTO TITLE
       ===================================================== */

    updateAutoTitle() {
        if (this.has_title) {
            return;
        }

        const title = [
            this.last_name,
            this.first_name,
        ]
            .filter(Boolean)
            .join(" ")
            .trim();


        this.setProperty(
            "title",
            title
        );
    }


    /* =====================================================
       PHONE
       ===================================================== */

    get is_valid_phone_number() {
        /*
         * Phone is optional for MMBR.
         * Requiredness for other roles is checked
         * separately in is_role_valid.
         */
        if (!this.phone_number) {
            return true;
        }


        const phone =
            normalizePhone(
                this.phone_number
            );


        if (!phone) {
            return false;
        }


        try {
            return isValidPhoneNumber(
                phone
            );
        } catch {
            return false;
        }
    }


    /* =====================================================
       ROLE VALIDATION
       ===================================================== */

    get is_role_valid() {
        if (!this.role_id) {
            return false;
        }
        /*
         * Backend requires phone_number for
         * every role except MMBR.
         */
        if (
            this.role_id !== "MMBR" &&
            !this.phone_number
        ) {
            return false;
        }
        /* -------------------------
           MEMBER
           ------------------------- */
        if (this.role_id === "MMBR") {
            return !isEmpty(
                this.year_born
            );

        }


        /* -------------------------
           PARENT
           ------------------------- */

        if (this.role_id === "PRNT") {

            /*
             * Exactly one parent type:
             *
             * mother=true, father=false
             * OR
             * mother=false, father=true
             */
            if (
                Boolean(this.is_mother) ===
                Boolean(this.is_father)
            ) {
                return false;
            }


            if (
                !Array.isArray(
                    this.children
                ) ||
                this.children.length === 0
            ) {
                return false;
            }


            /*
             * Children must already exist.
             * No temporary/new Person objects.
             */
            const invalidChild =
                this.children.some(
                    (child) =>
                        !(
                            child?.id ??
                            child?.company_user_id
                        )
                );


            if (invalidChild) {
                return false;
            }


            return true;
        }


        /*
         * MGR / TRN / MDC / SMM etc.
         * Common required fields are validated
         * in is_valid.
         */
        return true;
    }


    /* =====================================================
       GENERAL VALIDATION
       ===================================================== */

    get is_valid() {
    return Boolean(
        this.first_name?.trim() &&
        this.last_name?.trim() &&
        this.title?.trim() &&
        this.company_id &&
        this.is_valid_phone_number &&
        this.is_role_valid
    );
}


    validate() {
        this.was_validated = true;
        return this.is_valid;
    }


    /* =====================================================
       AVATAR
       ===================================================== */

    get avatar_valid() {
        return Boolean(
            this.photo_upload
        );
    }


    async upload_avatar() {
        if (!this.avatar_valid) {
            throw new Error(
                "please_check_data"
            );
        }


        const res =
            await post(
                "company/user/photo",
                this
            );


        if (!res?.avatar) {
            throw new Error(
                "save_error"
            );
        }


        this.setProperty(
            "avatar",
            res.avatar
        );


        this.setProperty(
            "photo_upload",
            null
        );


        return this;
    }
}